import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { Outlet, ScrollRestoration, useLocation, useNavigate } from "react-router";
import { useSessionRefresh } from "@ncp/api-client";
import { useLocale } from "@ncp/i18n";
import { SessionGuard } from "@ncp/assistant-sdk";
import { ConfirmProvider, Toaster } from "@ncp/ui";
import { WorkspaceProvider, useWorkspace } from "./workspace-context";
import { LEGACY_SHELL, SHELL } from "./flags";
import { withLocales } from "./locales-ready";
import { EconicShell } from "../econic/shell/EconicShell";
import { KeptPages } from "../pages-host/KeptPages";
import { UseCaseStarter } from "../pages-host/UseCaseStarter";
import { PageChromeProvider } from "../shell/chrome";
import { DockProvider } from "../shell/dock";
import { ShellStateProvider } from "../shell/state";

// The keyboard map loads the first time it is asked for (⌘/), and stays mounted after that so it can animate closed (bundle budget SH-22).
const ShortcutsDialog = lazy(withLocales(() => import("../components/ShortcutsDialog").then((m) => ({ default: m.ShortcutsDialog }))));

// The os and legacy frames load only under their flags, so none of their code weighs on the ECONIC bundle (shell-ia §18).
const Frame = lazy(() => import("../os/Frame").then((m) => ({ default: m.Frame })));
const WorkspaceShell = lazy(() => import("../shell/WorkspaceShell").then((m) => ({ default: m.WorkspaceShell })));
const CommandPalette = lazy(() => import("../components/CommandPalette").then((m) => ({ default: m.CommandPalette })));
const InlineDock = lazy(() => import("../shell/AiDock").then((m) => ({ default: () => <m.AiDock variant="inline" /> })));
const LegacyDockToggle = lazy(() => import("../shell/AiDock").then((m) => ({ default: m.DockToggle })));

export { useNewChat } from "./useNewChat";
export { LEGACY_SHELL };

export function RootLayout() {
  const { pathname } = useLocation();
  // Permissions and workspace memberships are data: re-sync the session so console edits apply without a new sign-in.
  useSessionRefresh();
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [pathname]);
  return (
    <>
      <ScrollRestoration />
      <Outlet />
    </>
  );
}

/**
 * The signed-in layout. `VITE_SHELL=econic` (the default) mounts the ECONIC frame (econic/shell/EconicShell.tsx), which also draws the ⌘K
 * search of shell-ia §6 (it reads the person's preferences); `os` the 11 Oct Agentic OS frame; `legacy` the pre-OS workspace shell (shell-ia §17). The workspace, the page chrome and the
 * dock providers are shared by all three.
 */
export function WorkspaceLayout() {
  const [palette, setPalette] = useState(false);
  const [shortcuts, setShortcuts] = useState(false);
  const [shortcutsAsked, setShortcutsAsked] = useState(false);
  useEffect(() => { if (shortcuts) setShortcutsAsked(true); }, [shortcuts]);
  const openPalette = useCallback(() => setPalette(true), []);
  const openShortcuts = useCallback(() => setShortcuts(true), []);
  return (
    <WorkspaceProvider onOpenPalette={openPalette} onOpenShortcuts={openShortcuts}>
      <ShellStateProvider>
        <PageChromeProvider>
          <DockProvider>
            {SHELL === "econic" ? (
              <EconicShell palette={palette} onPaletteChange={setPalette} />
            ) : SHELL === "legacy" ? (
              <LegacyShell />
            ) : (
              <Suspense fallback={null}><Frame /></Suspense>
            )}
            {SHELL === "econic" ? null : <PaletteHost open={palette} onOpenChange={setPalette} />}
            {shortcuts || shortcutsAsked ? <Suspense fallback={null}><ShortcutsDialog open={shortcuts} onOpenChange={setShortcuts} variant={SHELL} /></Suspense> : null}
            <UseCaseStarter />
          </DockProvider>
        </PageChromeProvider>
      </ShellStateProvider>
    </WorkspaceProvider>
  );
}

function PaletteHost({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { actions } = useWorkspace();
  return <Suspense fallback={null}><CommandPalette open={open} onOpenChange={onOpenChange} actions={actions} variant={SHELL === "legacy" ? "legacy" : "os"} /></Suspense>;
}

function LegacyShell() {
  const { dir } = useLocale();
  const navigate = useNavigate();
  return (
    <>
      <ConfirmProvider>
        <Suspense fallback={null}>
          <WorkspaceShell dock={<InlineDock />} dockToggle={<LegacyDockToggle />}>
            <KeptPages>
              <Outlet />
            </KeptPages>
          </WorkspaceShell>
        </Suspense>
      </ConfirmProvider>
      <SessionGuard onSignedOut={() => navigate("/login")} />
      <Toaster dir={dir} />
    </>
  );
}
